import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva } from "class-variance-authority";

const inlineLinkVariants = cva(
  [
    "font-(family-name:--font-family-title)",
    "font-(number:--font-weight-medium)",
    "cursor-pointer",
    "select-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "rounded-(--radius-sm)",
    // Focus ring
    "focus-visible:ring-2",
    "focus-visible:ring-(--color-inline-link-focus-ring)",
    "focus-visible:ring-offset-(--spacing-4)",
    // Pressed/active background
    "active:bg-(--color-inline-link-pressed-bg)",
  ],
  {
    variants: {
      size: {
        sm: [
          "text-(length:--font-size-title-sm)",
          "leading-(--font-line-height-20)",
        ],
        md: [
          "text-(length:--font-size-title-md)",
          "leading-(--font-line-height-24)",
        ],
      },
      variant: {
        /**
         * Contextual: triggers a full context change (new page, route, or view).
         * Renders blue; underline appears on hover only.
         */
        contextual: [
          "text-(--color-inline-link-contextual)",
          "no-underline",
          "hover:underline",
          "underline-offset-4",
        ],
        /**
         * Navigational: reveals information within the current context
         * (drawer, popover, inline expansion). Renders near-black; always underlined.
         */
        navigational: [
          "text-(--color-inline-link-navigational)",
          "text-start",
          "underline",
          "underline-offset-4",
          "hover:text-(--color-inline-link-navigational-hover)",
        ],
      },
      destructive: {
        true: [
          "text-(--color-text-destructive)",
          "hover:text-(--color-text-destructive)",
        ],
      },
      disabled: {
        true: ["opacity-50", "pointer-events-none"],
      },
    },
    defaultVariants: {
      size: "md",
      variant: "contextual",
    },
  },
);

type BaseProps = {
  /** Visual size of the link. Defaults to the medium 16px/24px variant. */
  size?: "sm" | "md";
  /** Whether to render in the destructive (error/overdue) state. */
  destructive?: boolean;
  /** Whether the link is non-interactive. Applies aria-disabled and suppresses interaction. */
  disabled?: boolean;
  children: React.ReactNode;
};

/**
 * **Contextual** — use `href` or `render` when clicking causes a full context change
 * (new page, route, or view). Renders blue; underline appears on hover only.
 *
 * Pass `render` to delegate to a router link (e.g. TanStack Router's `<Link>`):
 * ```tsx
 * <InlineLink render={<Link to="/assets/$id" params={{ id }} />}>View asset</InlineLink>
 * ```
 */
type ContextualProps = BaseProps & {
  /** Contextual: renders an `<a>` tag, blue text, underline on hover. */
  href?: string;
  render?: React.ReactElement;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
} & Omit<useRender.ComponentProps<"a">, "className" | "children" | "onClick">;

/**
 * **Navigational** — use `onClick` when clicking reveals information within the current
 * context (drawer, popover, inline expansion). Renders near-black; always underlined.
 */
type NavigationalProps = BaseProps & {
  /** Navigational: renders a `<button>`, near-black text, always underlined. */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  href?: never;
  render?: never;
} & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "className" | "children" | "onClick" | "disabled"
  >;

export type { NavigationalProps, ContextualProps };
export type InlineLinkProps = NavigationalProps | ContextualProps;

/**
 * Inline text link with two semantic variants determined by props:
 *
 * - **Contextual** (`href` or `render`) — triggers a full context change. Blue text,
 *   underline on hover. Use for routes, pages, or external URLs.
 * - **Navigational** (`onClick`) — reveals information within the current context.
 *   Near-black text, always underlined. Use for drawers, popovers, inline expansions.
 *
 * Font size is inherited from the surrounding context so the link sits correctly
 * inline in any body copy, table cell, or label.
 *
 * @example
 * // Contextual — plain href
 * <InlineLink href="/assets/123">View asset</InlineLink>
 *
 * @example
 * // Contextual — TanStack Router
 * <InlineLink render={<Link to="/assets/$id" params={{ id }} />}>View asset</InlineLink>
 *
 * @example
 * // Navigational — opens a drawer
 * <InlineLink onClick={() => openDrawer(id)}>See details</InlineLink>
 *
 * @example
 * // Destructive state
 * <InlineLink onClick={() => openDrawer(id)} destructive>Past due</InlineLink>
 */
function InlineLink(props: InlineLinkProps) {
  const {
    size = "md",
    destructive = false,
    disabled = false,
    children,
    ...rest
  } = props;

  const isNavigational =
    "onClick" in props &&
    props.onClick !== undefined &&
    !("href" in props && props.href !== undefined);

  const className = inlineLinkVariants({
    size,
    variant: isNavigational ? "navigational" : "contextual",
    destructive,
    disabled,
  });

  const { render, href, target, rel, ...anchorRest } = isNavigational
    ? ({} as ContextualProps)
    : (rest as ContextualProps);
  const resolvedRel = target === "_blank" && !rel ? "noopener noreferrer" : rel;
  const renderedAnchor = useRender({
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(anchorRest as useRender.ComponentProps<"a">, {
      // Only spread href/rel/target when defined — avoids clobbering props set
      // on the render element (e.g. a router Link that derives href from `to`).
      ...(href !== undefined && { href }),
      ...(target !== undefined && { target }),
      ...(resolvedRel !== undefined && { rel: resolvedRel }),
      // Disabled: remove href so keyboard Enter won't navigate, and block
      // any residual default action (e.g. from render-prop router links).
      // Note: render-prop router links must respect event.defaultPrevented
      // to honour this; links that navigate regardless are outside our control.
      ...(disabled && {
        href: undefined,
        tabIndex: -1,
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault(),
      }),
      "aria-disabled": disabled || undefined,
      className,
      children,
    }),
  });

  if (isNavigational) {
    const { onClick, ...buttonRest } = rest as NavigationalProps;
    return (
      <button
        {...(buttonRest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        type="button"
        onClick={disabled ? undefined : onClick}
        aria-disabled={disabled || undefined}
        className={className}
      >
        {children}
      </button>
    );
  }

  return renderedAnchor;
}

export { InlineLink };
